import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Folder, FileText, Image as ImageIcon, Download, UploadCloud, Search,
    HardDrive, ChevronRight, Loader2, Video, X, Calendar, User, Camera,
    Briefcase, CheckCircle2, Check, AlertTriangle, XCircle, Circle, Archive,
    Layers, Tag, PlayCircle, FolderOpen, Package, FolderPlus, Trash2, Cloud,
    ShieldCheck, FileCheck, Users
} from 'lucide-react';
import { FilesService, PraticaService, IncaricoService, ArchivioService, PersonalDriveService, ClientService, SettingsService, DocUfficioService } from '../services/apiService';
import { MODULI_IN_BIANCO, ModuloInBianco, generaModuloInBianco, impostazioniPerPdf } from '../utils/moduliInBianco';
import { mapWithConcurrency, classifyMediaDeleteResponse, summarizeBatchDelete } from '../utils/batchDelete';
import { useToast } from '../components/ToastContext';
import { Modal } from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { FileAnalysisResultDto, UserRole } from '../types';
import { UploadSummaryModal } from '../components/UploadSummaryModal';
import { PdfThumb } from '../components/PdfThumb';
import { isBrowserPlayableVideoUrl } from '../utils/videoPlayback';
import { isPreviewableImage, isConvertibleImageExt, copiaInAltroFormato } from '../utils/imagePreview';
import { downloadAttachmentUrl } from '../utils/download';

const extractUrl = (res: any): string =>
    typeof res === 'string' ? res : (res?.data?.data || res?.data || res?.url || '');

type UploadStatus = 'pending' | 'uploading' | 'ready' | 'needs_date' | 'error';
type ModalStep = 'select' | 'confirm' | 'uploading' | 'confirm_dates' | 'completed';

const DEVICE_OPTIONS = ['FOTOCAMERA', 'VIDEOCAMERA', 'SMARTPHONE', 'SPY', 'ALTRO'] as const;
type DeviceOption = typeof DEVICE_OPTIONS[number];

interface UploadItem {
    file: File;
    status: UploadStatus;
    tempId?: string;
    detectedDate?: string;
    manualDate: string;
    error?: string;
}

const runConcurrent = async (tasks: (() => Promise<void>)[], limit: number) => {
    let i = 0;
    const worker = async () => { while (i < tasks.length) await tasks[i++](); };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
};

const isVideoExt = (name?: string) =>
    ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'].includes((name?.split('.').pop() || '').toLowerCase());

// Cartella dei media senza soggetto (mandati con più soggetti), stessa etichetta dell'app.
const NO_SOGG = 'NON SPECIFICATO';

export const FileManagerView: React.FC = () => {
    const { addToast } = useToast();
    const { user } = useAuth();

    // Dati
    const [pratiche, setPratiche] = useState<any[]>([]);
    const [praticaIncarichi, setPraticaIncarichi] = useState<any[]>([]);
    const [incarichiMedia, setIncarichiMedia] = useState<any[]>([]);
    const [officeFiles, setOfficeFiles] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Navigazione
    const [currentPath, setCurrentPath] = useState('ROOT');
    const pathSegments = useMemo(() => currentPath.split('/'), [currentPath]);
    const [searchQuery, setSearchQuery] = useState('');

    // Anteprima
    const [previewFile, setPreviewFile] = useState<any | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    // Il <video> dell'anteprima non riesce a riprodurre l'URL (formato/codec).
    const [previewVideoError, setPreviewVideoError] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);

    // Upload
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState<ModalStep>('select');
    const [uploadType, setUploadType] = useState<'ufficio' | 'media'>('media');
    const [uploadCategoria, setUploadCategoria] = useState<DeviceOption>('SMARTPHONE');
    const [uploadSoggetto, setUploadSoggetto] = useState<string>('');
    const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [completedSummary, setCompletedSummary] = useState<{ ok: number; errors: number } | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    // Watermark sì/no per il batch, come in OpsTab/TabMedia. Default true, si
    // resetta alla chiusura e a ogni nuovo batch.
    const [applyDateWatermark, setApplyDateWatermark] = useState(true);

    // Velocità e avanzamento upload
    const [uploadSpeedMBps, setUploadSpeedMBps] = useState<number | null>(null);
    const [uploadTotalBytes, setUploadTotalBytes] = useState(0);
    const [uploadedBytes, setUploadedBytes] = useState(0);
    const speedWindowRef = useRef<{ t: number; b: number }[]>([]);
    const uploadedBytesRef = useRef(0);

    const addBytesProgress = (bytes: number) => {
        uploadedBytesRef.current += bytes;
        setUploadedBytes(uploadedBytesRef.current);
        const now = Date.now();
        speedWindowRef.current.push({ t: now, b: bytes });
        const cutoff = now - 3000;
        speedWindowRef.current = speedWindowRef.current.filter(x => x.t > cutoff);
        const totalB = speedWindowRef.current.reduce((s, x) => s + x.b, 0);
        const windowMs = now - (speedWindowRef.current[0]?.t ?? now);
        setUploadSpeedMBps(windowMs > 200 ? (totalB / windowMs * 1000) / 1048576 : null);
    };

    // Modale riepilogo
    const [summaryResults, setSummaryResults] = useState<FileAnalysisResultDto[]>([]);
    const [showSummary, setShowSummary] = useState(false);
    // Media caricati ma nascosti dalla galleria (senza data o duplicati) finché
    // non si completa la data o si decide se tenerli: li recuperiamo qui.
    const [recoveryItems, setRecoveryItems] = useState<FileAnalysisResultDto[]>([]);
    const [recoveryOpen, setRecoveryOpen] = useState(false);
    const [summaryIncaricoId, setSummaryIncaricoId] = useState(0);

    // Eliminazione pratica: il titolare pulisce periodicamente i mandati archiviati
    // (soft-delete, solo ADMIN). Doppio passaggio con modale e parola "ELIMINA"
    // per evitare cancellazioni accidentali.
    const [showDeletePraticaModal, setShowDeletePraticaModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingPratica, setIsDeletingPratica] = useState(false);
    // Svuotamento selettivo: categorie scelte dal titolare, con i conteggi caricati
    // all'apertura del modale.
    const [svuotaOpts, setSvuotaOpts] = useState<{ prove: boolean; fotoKm: boolean; documenti: boolean }>({ prove: true, fotoKm: false, documenti: false });
    const [svuotaCounts, setSvuotaCounts] = useState<{ prove: number; fotoKm: number; documenti: number } | null>(null);

    // Apre il modale e carica i conteggi per categoria.
    const openSvuotaModal = async () => {
        if (!praticaId) return;
        setDeleteConfirmText('');
        setSvuotaOpts({ prove: true, fotoKm: false, documenti: false });
        setSvuotaCounts(null);
        setShowDeletePraticaModal(true);
        try {
            const res = await PraticaService.mediaCounts(praticaId);
            if (res?.success) setSvuotaCounts(res.data);
        } catch { /* I conteggi sono solo informativi: il modale funziona anche senza */ }
    };

    // ZIP
    const [zipModalOpen, setZipModalOpen] = useState(false);
    const [zipPlan, setZipPlan] = useState<{ totalFiles: number; totalParts: number; fullToken?: string | null; parts: { token: string; fileCount: number; partNumber: number }[] } | null>(null);
    const [isGeneratingZip, setIsGeneratingZip] = useState(false);
    // true = originali (senza watermark né timestamp), false = con timestamp/watermark,
    // null = scelta non ancora fatta.
    const [zipOriginals, setZipOriginals] = useState<boolean | null>(null);

    // Drive personale
    const [personalItems, setPersonalItems] = useState<any[]>([]);
    const [isPersonalLoading, setIsPersonalLoading] = useState(false);
    const [personalFolderNames, setPersonalFolderNames] = useState<Record<number, string>>({});
    const [newFolderModalOpen, setNewFolderModalOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);

    // Selezione multipla per eliminazione. Chiave "tipo:id" (media | office | pfile | pfolder)
    // perché nella stessa vista convivono entità con id che possono coincidere.
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);
    const toggleKey = (key: string) => setSelectedKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const clearSelection = () => setSelectedKeys(new Set());
    // La selezione vale solo nella cartella corrente: si azzera al cambio path.
    useEffect(() => { setSelectedKeys(new Set()); }, [currentPath]);

    const isPersonalDrive = useMemo(() => pathSegments.includes('PERSONAL'), [pathSegments]);
    // "Doc. Ufficio" in radice: moduli in bianco generati al momento (vedi moduliInBianco).
    const isModuli = useMemo(() => pathSegments[1] === 'MODULI', [pathSegments]);
    const [moduloInCorso, setModuloInCorso] = useState<ModuloInBianco | null>(null);
    const impostazioniModuliRef = useRef<any>(null);
    const isCollaboratori = isModuli && pathSegments[2] === 'COLLABORATORI';
    const [documentiCollab, setDocumentiCollab] = useState<{ id: number; tipo: string; titolo: string; versione: number; inVigoreDal?: string }[] | null>(null);
    const [documentoInCorso, setDocumentoInCorso] = useState<number | null>(null);
    useEffect(() => {
        if (!isCollaboratori) return;
        setDocumentiCollab(null);
        DocUfficioService.collaboratori()
            .then(setDocumentiCollab)
            .catch(() => { setDocumentiCollab([]); addToast('Documenti dei collaboratori non caricati', 'error'); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCollaboratori]);
    const currentPersonalFolderId = useMemo<number | null>(() => {
        if (!isPersonalDrive) return null;
        for (let i = pathSegments.length - 1; i >= 0; i--) {
            const s = pathSegments[i];
            if (s.startsWith('PD_')) return Number(s.slice(3));
        }
        return null;
    }, [isPersonalDrive, pathSegments]);

    useEffect(() => { loadAll(); }, []);

    useEffect(() => {
        const praticaSeg = pathSegments.find(s => s.startsWith('PRATICA_'));
        if (!praticaSeg) { setIncarichiMedia([]); setPraticaIncarichi([]); setOfficeFiles([]); setRecoveryItems([]); return; }
        const praticaId = Number(praticaSeg.split('_')[1]);
        if (!praticaId) return;

        let cancelled = false;
        (async () => {
            try {
                // Includiamo gli AUTOGENERATO: gli upload da FileManager finiscono in un
                // incarico AUTOGENERATO, senza il flag non si vedrebbero.
                const [incRes, filesRes] = await Promise.all([
                    IncaricoService.getByPratica(praticaId, { includeAutogenerati: true }),
                    FilesService.getAll(),
                ]);
                if (cancelled) return;

                const incarichi: any[] = incRes.data || incRes || [];
                setPraticaIncarichi(incarichi);

                // Media con categoria → albero date; senza categoria → DOC. UFFICIO
                const media: any[] = [];
                const docMedia: any[] = [];
                // Media caricati ma nascosti (senza data o duplicati) da completare. Esclusi
                // quelli in approvazione del collaboratore (OUTSIDE_RANGE / PENDING_APPROVAL),
                // che si gestiscono da Approvazioni.
                const recovery: FileAnalysisResultDto[] = [];
                // Livello "Soggetto investigato" dell'albero, come MediaFolderTree nell'app:
                // soggetto dell'incarico; se la pratica ne ha uno solo vale anche per gli
                // incarichi senza soggetto, altrimenti NO_SOGG.
                const soggSet = new Set<string>();
                incarichi.forEach((inc: any) => { if (inc.soggettoNome) soggSet.add(inc.soggettoNome); });
                const soloSoggetto = soggSet.size === 1 ? Array.from(soggSet)[0] : null;
                incarichi.forEach((inc: any) => {
                    const collaboratoreName = inc.collaboratore
                        ? `${inc.collaboratore.name} ${inc.collaboratore.surname}` : 'Collaboratore';
                    const soggettoName: string = inc.soggettoNome || soloSoggetto || NO_SOGG;
                    // Data della cartella a cascata: report → inizio incarico → data reale/EXIF.
                    // L'ultimo caso copre gli upload da TabMedia/FileManager (incarico AUTOGENERATO).
                    const reportDateStr: string | undefined = inc.relazioni?.[0]?.dataRelazione;
                    const reportDate: Date | null = reportDateStr ? new Date(`${reportDateStr}T12:00:00`) : null;
                    const isAutogen = inc.tipoOrigine === 'AUTOGENERATO';
                    const startStr: string | undefined = inc.dataInizio;
                    const startDate: Date | null = (!isAutogen && startStr) ? new Date(startStr) : null;
                    (inc.media || []).forEach((m: any) => {
                        if (m.filename?.startsWith('Km_')) return;
                        {
                            const url = m.urlCompressed || m.finalUrl || m.urlOriginal || null;
                            // Esclude gli elementi gestiti da Approvazioni (fuori periodo o data proposta
                            // dal collaboratore).
                            const isApprovalItem = m.analysisStatus === 'OUTSIDE_RANGE'
                                || m.analysisStatus === 'PENDING_APPROVAL'
                                || !!m.pendingApprovalReason || !!m.pendingApprovalAt;
                            const needsRecovery = !!url && !isApprovalItem
                                && (!m.dataAcquisizione || m.analysisStatus === 'DUPLICATE');
                            if (needsRecovery) {
                                const ext = (m.filename?.split('.').pop() || '').toLowerCase();
                                const isVid = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext);
                                recovery.push({
                                    mediaId: m.id,
                                    name: m.filename || `media_${m.id}`,
                                    analysisStatus: m.analysisStatus === 'DUPLICATE' ? 'DUPLICATE' : 'NO_EXIF',
                                    tempId: `rec-${m.id}`,
                                    thumbnailUrl: url,
                                    fileType: isVid ? 'video/mp4' : 'image/jpeg',
                                } as FileAnalysisResultDto);
                            }
                        }
                        // Nasconde gli upload senza data di acquisizione confermata
                        if (!m.dataAcquisizione) return;
                        // Nasconde i media in attesa di approvazione
                        if (m.analysisStatus === 'DUPLICATE' || m.analysisStatus === 'OUTSIDE_RANGE' || m.analysisStatus === 'PENDING_APPROVAL') return;
                        const urlCompressed = m.urlCompressed || m.finalUrl || null;
                        const urlOriginal = m.urlOriginal || m.finalUrl || null;
                        if (!urlCompressed && !urlOriginal) return; // Originale non ancora su S3
                        const categoryName = m.categoria
                            || m.filename?.match(/^\[(.*?)\]/)?.[1]?.toUpperCase()
                            || null;
                        const item = {
                            id: m.id, isMedia: true,
                            name: m.filename || `media_${m.id}`,
                            ext: (m.filename?.split('.').pop() || '').toLowerCase(),
                            urlCompressed,
                            urlOriginal,
                            analysisStatus: m.analysisStatus,
                            dataAcquisizione: m.dataAcquisizione || m.uploadTime,
                            uploaderName: m.uploaderName && m.uploaderName !== 'UFFICIO AMMINISTRATIVO'
                                ? m.uploaderName : collaboratoreName,
                            soggettoName,
                            praticaId,
                            // dateObj è la data reale del file (mostrata), folderDate quella del report
                            // (cartella Anno/Mese/Giorno).
                            dateObj: new Date(m.dataAcquisizione || m.uploadTime || m.createdAt),
                            folderDate: reportDate ?? startDate ?? new Date(m.dataAcquisizione || m.uploadTime || m.createdAt),
                            categoryName: categoryName || 'DOC. UFFICIO',
                            cleanName: m.filename?.replace(/^\[.*?\]\s*/, '') || m.filename || `media_${m.id}`,
                        };
                        if (categoryName) {
                            media.push(item);
                        } else {
                            docMedia.push(item); // Senza categoria → DOC. UFFICIO
                        }
                    });
                });
                setIncarichiMedia(media);
                setRecoveryItems(recovery);

                const allFiles: any[] = filesRes.data || filesRes || [];
                // Per i mandati investigativi mostriamo anche il PDF DatiSoggetto in Doc Ufficio,
                // utile per ritrovare l'anagrafica del soggetto. Per gli altri tipi resta nascosto.
                const currentPratica = pratiche.find((p: any) => p.id === praticaId);
                const isInvestigativo = currentPratica?.modelId === 'mand-investigativo';
                const excludeRe = isInvestigativo
                    ? /^(privacy_|docid_|frame_)/i
                    : /^(datisoggetto_|privacy_|docid_|frame_)/i;
                const pFiles = allFiles.filter((f: any) => f.praticaId === praticaId && !f.name?.match(excludeRe));

                // Documenti del cliente collegato, letti dall'anagrafica e mostrati in DOC. UFFICIO.
                // FilesDto non espone clientId, quindi recuperiamo il cliente della pratica che
                // porta con sé i suoi file: eventuali modifiche in anagrafica si vedono qui.
                let clientDocs: any[] = [];
                // La lista pratiche (PraticaListDTO) espone `clienteId`, il DTO completo `clientId`.
                const praticaClientId = currentPratica?.clientId ?? currentPratica?.clienteId;
                if (praticaClientId) {
                    try {
                        const cliRes: any = await ClientService.getById(praticaClientId);
                        const cli: any = cliRes?.data ?? cliRes;
                        const cFiles: any[] = cli?.files || [];
                        clientDocs = cFiles
                            .filter((f: any) => f && (f.urlCompressed || f.urlOriginal) && !f.deleted)
                            .map((f: any) => {
                                const cleanName = (f.name || `documento_${f.id}`).replace(/^DocID_/i, '');
                                return {
                                    id: f.id, isMedia: false,
                                    name: f.name || `documento_${f.id}`,
                                    ext: (f.name?.split('.').pop() || '').toLowerCase(),
                                    urlCompressed: f.urlCompressed || f.urlOriginal,
                                    dataAcquisizione: f.dataAcquisizione || f.createdAt,
                                    uploaderName: 'DOCUMENTO CLIENTE',
                                    praticaId,
                                    isClientDoc: true,
                                    dateObj: new Date(f.dataAcquisizione || f.createdAt),
                                    folderDate: new Date(f.dataAcquisizione || f.createdAt),
                                    categoryName: 'DOC. UFFICIO',
                                    cleanName,
                                };
                            });
                    } catch { /* Documenti cliente opzionali: non bloccano la vista */ }
                }

                setOfficeFiles([
                    ...pFiles.map((f: any) => ({
                        id: f.id, isMedia: false,
                        name: f.name || `file_${f.id}`,
                        ext: (f.name?.split('.').pop() || '').toLowerCase(),
                        urlCompressed: f.urlCompressed || f.urlOriginal,
                        dataAcquisizione: f.dataAcquisizione || f.createdAt,
                        uploaderName: f.uploaderName || 'UFFICIO AMMINISTRATIVO',
                        praticaId,
                        dateObj: new Date(f.dataAcquisizione || f.createdAt),
                        folderDate: new Date(f.dataAcquisizione || f.createdAt),
                        categoryName: 'DOC. UFFICIO',
                        cleanName: f.name || `file_${f.id}`,
                    })),
                    ...docMedia,
                    ...clientDocs,
                ]);
            } catch (e) {
                console.error('Errore caricamento media:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [currentPath, reloadKey, pratiche]);

    // Mandati paginati (100 per pagina, già ordinati per createdAt DESC dal BE)
    // con "Carica altri". La ricerca filtra quelli già caricati.
    const [fmPage, setFmPage] = useState(0);
    const [fmHasMore, setFmHasMore] = useState(false);
    const [fmLoadingMore, setFmLoadingMore] = useState(false);
    const loadAll = async (page = 0, append = false) => {
        if (append) setFmLoadingMore(true); else setIsLoading(true);
        try {
            const res = await PraticaService.getAll({ size: 100, page });
            const content = res.data?.content || (Array.isArray(res.data) ? res.data : []);
            setPratiche(prev => append ? [...prev, ...content] : content);
            setFmPage(page);
            setFmHasMore(res.data?.last === false);
        } catch { addToast('Errore caricamento pratiche', 'error'); }
        finally { if (append) setFmLoadingMore(false); else setIsLoading(false); }
    };

    useEffect(() => {
        if (!isPersonalDrive) { setPersonalItems([]); return; }
        let cancelled = false;
        setIsPersonalLoading(true);
        (async () => {
            try {
                const items = await PersonalDriveService.list(currentPersonalFolderId);
                if (cancelled) return;
                setPersonalItems(items);
                setPersonalFolderNames(prev => {
                    const next = { ...prev };
                    items.filter(i => i.folder).forEach(f => { next[f.id] = f.name; });
                    return next;
                });
            } catch {
                if (!cancelled) addToast('Errore caricamento Drive Personale', 'error');
            } finally {
                if (!cancelled) setIsPersonalLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isPersonalDrive, currentPersonalFolderId, reloadKey]);

    // Helper navigazione
    const navigateTo = (seg: string) => setCurrentPath(p => `${p}/${seg}`);
    const navigateBackTo = (idx: number) => setCurrentPath(pathSegments.slice(0, idx + 1).join('/'));
    const praticaId = useMemo(() => {
        const seg = pathSegments.find(s => s.startsWith('PRATICA_'));
        return seg ? Number(seg.split('_')[1]) : null;
    }, [pathSegments]);
    const praticaName = useMemo(() => {
        if (!praticaId) return '';
        const p = pratiche.find(x => x.id === praticaId);
        return p?.name || `Mandato #${praticaId}`;
    }, [praticaId, pratiche]);

    const getBreadcrumbLabel = (seg: string) => {
        if (seg === 'ROOT') return 'ARCHIVIO';
        if (seg === 'PERSONAL') return 'DRIVE PERSONALE';
        if (seg === 'MODULI') return 'DOC. UFFICIO';
        if (seg === 'COLLABORATORI') return 'COLLABORATORI';
        if (seg.startsWith('PRATICA_')) return praticaName || seg;
        if (seg.startsWith('PD_')) {
            const id = Number(seg.slice(3));
            return personalFolderNames[id] || seg;
        }
        return seg;
    };

    // File system virtuale
    const allFilesFlat = useMemo(() => [...incarichiMedia, ...officeFiles], [incarichiMedia, officeFiles]);

    const viewData = useMemo(() => {
        if (isCollaboratori) return { type: 'COLLABORATORI' as const, items: [], icon: null };
        if (isModuli) return { type: 'MODULI' as const, items: [], icon: null };
        if (isPersonalDrive) {
            const folders = personalItems.filter(i => i.folder);
            const files = personalItems.filter(i => !i.folder).map(i => ({
                id: i.id,
                isMedia: false,
                isPersonal: true,
                name: i.name,
                cleanName: i.name,
                ext: (i.extension || i.name?.split('.').pop() || '').toLowerCase(),
                urlCompressed: i.url,
                urlOriginal: i.url,
                dataAcquisizione: i.createdAt,
                uploaderName: '',
                dateObj: new Date(i.createdAt),
                categoryName: 'PERSONAL',
            }));
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return {
                    type: 'PERSONAL_DRIVE' as const,
                    folders: folders.filter(f => f.name.toLowerCase().includes(q)),
                    files: files.filter(f => f.cleanName.toLowerCase().includes(q)),
                    icon: null,
                };
            }
            return { type: 'PERSONAL_DRIVE' as const, folders, files, icon: null };
        }

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return {
                type: 'FILES' as const,
                items: allFilesFlat.filter(f => f.cleanName.toLowerCase().includes(q)),
                icon: null,
            };
        }

        const depth = pathSegments.length;

        if (depth === 1) {
            return { type: 'ROOT' as const, items: [], icon: null };
        }

        const praticaSegIdx = pathSegments.findIndex(s => s.startsWith('PRATICA_'));
        if (praticaSegIdx === -1) return { type: 'ROOT' as const, items: [], icon: null };

        const relDepth = depth - praticaSegIdx - 1;

        const isUfficio = pathSegments[praticaSegIdx + 1] === 'DOC. UFFICIO';

        if (relDepth === 0) {
            // Primo livello: DOC. UFFICIO + anni
            const years = Array.from(new Set(incarichiMedia.map(m => (m.folderDate ?? m.dateObj).getFullYear().toString()))).sort((a, b) => b.localeCompare(a));
            return {
                type: 'FIRST_LEVEL' as const,
                years,
                hasUfficio: officeFiles.length > 0,
                icon: null,
            };
        }

        if (isUfficio) {
            return { type: 'FILES' as const, items: officeFiles, icon: null };
        }

        const filteredMedia = incarichiMedia.filter(m => {
            const d = m.folderDate ?? m.dateObj; // Cartella sulla data del report
            if (relDepth >= 1 && d.getFullYear().toString() !== pathSegments[praticaSegIdx + 1]) return false;
            if (relDepth >= 2 && d.toLocaleString('it-IT', { month: 'long' }).toUpperCase() !== pathSegments[praticaSegIdx + 2]) return false;
            if (relDepth >= 3 && d.getDate().toString().padStart(2, '0') !== pathSegments[praticaSegIdx + 3]) return false;
            if (relDepth >= 4 && m.categoryName !== pathSegments[praticaSegIdx + 4]) return false;
            if (relDepth >= 5 && m.soggettoName !== pathSegments[praticaSegIdx + 5]) return false;
            if (relDepth >= 6 && m.uploaderName !== pathSegments[praticaSegIdx + 6]) return false;
            return true;
        });

        if (relDepth === 1) return { type: 'FOLDERS' as const, items: Array.from(new Set(filteredMedia.map(m => (m.folderDate ?? m.dateObj).toLocaleString('it-IT', { month: 'long' }).toUpperCase()))), icon: <Calendar className="text-blue-500" size={28} /> };
        if (relDepth === 2) return { type: 'FOLDERS' as const, items: Array.from(new Set(filteredMedia.map(m => (m.folderDate ?? m.dateObj).getDate().toString().padStart(2, '0')))).sort(), icon: <Calendar className="text-emerald-500" size={28} /> };
        if (relDepth === 3) return { type: 'FOLDERS' as const, items: Array.from(new Set(filteredMedia.map(m => m.categoryName))), icon: <Tag className="text-amber-500" size={28} /> };
        if (relDepth === 4) return { type: 'FOLDERS' as const, items: Array.from(new Set(filteredMedia.map(m => m.soggettoName))), icon: <Users className="text-rose-500" size={28} /> };
        if (relDepth === 5) return { type: 'FOLDERS' as const, items: Array.from(new Set(filteredMedia.map(m => m.uploaderName))), icon: <User className="text-slate-500" size={28} /> };

        return { type: 'FILES' as const, items: filteredMedia, icon: null };
    }, [pathSegments, allFilesFlat, incarichiMedia, officeFiles, searchQuery, isPersonalDrive, personalItems, isModuli, isCollaboratori]);

    // Tutti i file nell'albero corrente (per ZIP)
    const downloadableItems = useMemo(() => {
        if (!praticaId) return { mediaIds: [], fileIds: [] };

        const splitItems = (items: any[]) => ({
            mediaIds: items.filter(f => f.isMedia).map(f => f.id),
            fileIds: items.filter(f => !f.isMedia).map(f => f.id),
        });

        if (viewData.type === 'ROOT' || viewData.type === 'FIRST_LEVEL') {
            return splitItems([...incarichiMedia, ...officeFiles]);
        }
        if (viewData.type === 'FILES') {
            return splitItems(viewData.items as any[]);
        }
        // Cartelle: tutti i file del sottoalbero corrente
        const isUfficio = pathSegments.includes('DOC. UFFICIO');
        if (isUfficio) return splitItems(officeFiles);
        const praticaSegIdx = pathSegments.findIndex(s => s.startsWith('PRATICA_'));
        const relDepth = pathSegments.length - praticaSegIdx - 1;
        const filtered = incarichiMedia.filter(m => {
            const d = m.folderDate ?? m.dateObj; // Cartella sulla data del report
            if (relDepth >= 1 && d.getFullYear().toString() !== pathSegments[praticaSegIdx + 1]) return false;
            if (relDepth >= 2 && d.toLocaleString('it-IT', { month: 'long' }).toUpperCase() !== pathSegments[praticaSegIdx + 2]) return false;
            if (relDepth >= 3 && d.getDate().toString().padStart(2, '0') !== pathSegments[praticaSegIdx + 3]) return false;
            // Lo ZIP rispetta la cartella corrente (categoria/soggetto/collaboratore),
            // non tutto il giorno.
            if (relDepth >= 4 && m.categoryName !== pathSegments[praticaSegIdx + 4]) return false;
            if (relDepth >= 5 && m.soggettoName !== pathSegments[praticaSegIdx + 5]) return false;
            return true;
        });
        return splitItems(filtered);
    }, [praticaId, viewData, incarichiMedia, officeFiles, pathSegments]);

    // ZIP in due passi: "Scarica ZIP" apre la scelta tra originali e versione con
    // watermark; il piano si genera solo dopo la conferma, così il backend riceve
    // già la variante giusta.
    const handleOpenZipModal = () => {
        if (!downloadableItems.mediaIds.length && !downloadableItems.fileIds.length) {
            addToast('Nessun file da scaricare', 'error');
            return;
        }
        setZipPlan(null);
        setZipOriginals(null);
        setZipModalOpen(true);
    };

    const handleConfirmZipChoice = async (originals: boolean) => {
        setZipOriginals(originals);
        setIsGeneratingZip(true);
        setZipPlan(null);
        try {
            const plan = await ArchivioService.planZip(
                downloadableItems.mediaIds,
                downloadableItems.fileIds,
                praticaName || 'archivio',
                originals,
            );
            setZipPlan(plan);
        } catch {
            addToast('Errore generazione piano ZIP', 'error');
            setZipOriginals(null);
        } finally {
            setIsGeneratingZip(false);
        }
    };

    // Anteprima
    // "Scarica" del pannello di anteprima: chiede un link in attachment al momento del clic,
    // così il file si scarica invece di aprirsi in una nuova pagina (e il link non è scaduto).
    const handleDownloadPreview = async (file: any) => {
        try {
            let url: string | undefined;
            if (file.isPersonal) url = await PersonalDriveService.getDownloadUrl(file.id, true);
            else if (file.isMedia) url = await IncaricoService.getMediaUrl(file.id, true);
            else {
                const res: any = await FilesService.getDownloadUrl(file.id, false, true);
                url = res?.data?.data || res?.data;
            }
            if (url) { downloadAttachmentUrl(url); return; }
        } catch { /* fallback sotto */ }
        if (previewUrl) window.open(previewUrl, '_blank');
    };

    const openPreview = async (file: any) => {
        setPreviewFile(file);
        setIsPreviewLoading(true);
        setPreviewUrl(null);
        setPreviewVideoError(false);
        try {
            if (file.isPersonal) {
                const url = await PersonalDriveService.getDownloadUrl(file.id);
                setPreviewUrl(url || file.urlOriginal);
            } else if (file.isMedia) {
                // Per i video usiamo l'URL compresso (MP4 convertito dal backend): l'originale
                // (MTS/MOV/AVI...) spesso il browser non lo riproduce.
                if (isVideoExt(file.name) && file.urlCompressed) {
                    setPreviewUrl(file.urlCompressed);
                } else {
                    const url = await IncaricoService.getMediaUrl(file.id);
                    setPreviewUrl(url || file.urlOriginal);
                }
            } else {
                // preview=true usa la copia convertita se presente: MP4 per i video (.MTS ecc.),
                // JPG per le foto che il browser non apre (RAW, HEIC).
                const res = await FilesService.getDownloadUrl(file.id, isVideoExt(file.name) || isConvertibleImageExt(file.ext));
                setPreviewUrl(res?.data?.data || res?.data || file.urlOriginal);
            }
        } catch { setPreviewUrl(file.urlCompressed || file.urlOriginal); }
        finally { setIsPreviewLoading(false); }
    };

    // Upload
    const updateItem = (index: number, patch: Partial<UploadItem>) =>
        setUploadItems(prev => prev.map((item, i) => i === index ? { ...item, ...patch } : item));
    const closeModal = () => { setIsUploadModalOpen(false); setModalStep('select'); setUploadItems([]); setUploadSoggetto(''); setCompletedSummary(null); setUploadSpeedMBps(null); setUploadedBytes(0); setUploadTotalBytes(0); speedWindowRef.current = []; uploadedBytesRef.current = 0; setApplyDateWatermark(true); };
    const closeCompleted = () => { closeModal(); setReloadKey(k => k + 1); };

    // Soggetti della pratica corrente (da datiAggiuntiviJson)
    const soggettiList = useMemo((): { nome: string }[] => {
        if (!praticaId) return [];
        const p = pratiche.find(x => x.id === praticaId);
        try {
            const dati = typeof p?.datiAggiuntiviJson === 'string'
                ? JSON.parse(p.datiAggiuntiviJson)
                : p?.datiAggiuntiviJson || {};
            if (Array.isArray(dati.soggetti)) return dati.soggetti;
            if (dati.soggetto_nome || dati.soggetto_denominazione)
                return [{ nome: dati.soggetto_nome || dati.soggetto_denominazione }];
        } catch {}
        return [];
    }, [praticaId, pratiche]);
    const uploadProgress = useMemo(() => {
        if (!uploadItems.length) return 0;
        return Math.round((uploadItems.filter(i => ['ready', 'needs_date', 'error'].includes(i.status)).length / uploadItems.length) * 100);
    }, [uploadItems]);

    const startUpload = async () => {
        if (!isPersonalDrive && !praticaId) { addToast('Seleziona prima una pratica', 'error'); return; }

        if (!isPersonalDrive && uploadType === 'media' && soggettiList.length > 1 && !uploadSoggetto) {
            addToast('Seleziona il soggetto investigato', 'error');
            return;
        }

        setModalStep('uploading');
        speedWindowRef.current = [];
        uploadedBytesRef.current = 0;
        setUploadSpeedMBps(null);
        setUploadedBytes(0);
        const totalBytes = uploadItems.reduce((s, i) => s + i.file.size, 0);
        setUploadTotalBytes(totalBytes);

        if (isPersonalDrive) {
            const results: UploadStatus[] = new Array(uploadItems.length).fill('pending');
            const tasks = uploadItems.map((item, i) => async () => {
                updateItem(i, { status: 'uploading' });
                try {
                    await PersonalDriveService.upload(item.file, currentPersonalFolderId, addBytesProgress);
                    results[i] = 'ready';
                    updateItem(i, { status: 'ready' });
                } catch (err: any) {
                    results[i] = 'error';
                    updateItem(i, { status: 'error', error: err?.response?.data?.message || 'Errore' });
                }
            });
            await runConcurrent(tasks, 4);
            const ok = results.filter(r => r === 'ready').length;
            const errors = results.filter(r => r === 'error').length;
            setCompletedSummary({ ok, errors });
            setModalStep('completed');
            return;
        }
        let targetIncaricoId = 0;
        if (uploadType === 'media') {
            const targetSoggetto = soggettiList.length > 1
                ? uploadSoggetto
                : (soggettiList[0]?.nome || '');
            // Solo un contenitore AUTOGENERATO (come fa l'app): in un incarico vero i file
            // finirebbero nella cartella della sua data invece che in quella del file.
            const mio = praticaIncarichi.find(inc =>
                inc.collaboratore?.id === user?.id
                && (inc as any).tipoOrigine === 'AUTOGENERATO'
                && (!targetSoggetto || (inc as any).soggettoNome === targetSoggetto)
            );
            targetIncaricoId = mio?.id ?? 0;
            if (!targetIncaricoId) {
                try {
                    const r = await IncaricoService.createIncaricoRapido(
                        praticaId!, 'Upload Cloud Titolare', targetSoggetto || undefined
                    );
                    targetIncaricoId = r?.data?.id ?? r?.id;
                } catch (err: any) {
                    addToast(`Errore creazione incarico: ${err.message}`, 'error');
                    setModalStep('select');
                    return;
                }
            }
        }
        const results: UploadStatus[] = new Array(uploadItems.length).fill('pending');
        const analysisResults: FileAnalysisResultDto[] = [];

        // Flag watermark del batch corrente.
        const watermarkChoice = applyDateWatermark;

        const tasks = uploadItems.map((item, i) => async () => {
            updateItem(i, { status: 'uploading' });
            try {
                let res: any;
                if (uploadType === 'ufficio') {
                    // Doc Ufficio senza watermark: sono documenti amministrativi, non prove.
                    // categoria='DOC. UFFICIO' li esclude dalla galleria "Documentazione Fotografica
                    // Mandato" che il collaboratore vede in InfoTab (lì vanno solo i file scelti
                    // in creazione/modifica del mandato, senza categoria).
                    await FilesService.uploadToPratica(praticaId!, item.file, false, 'DOC. UFFICIO');
                    results[i] = 'ready';
                    updateItem(i, { status: 'ready' });
                } else {
                    res = await IncaricoService.uploadMedia(targetIncaricoId, item.file, uploadCategoria, undefined, addBytesProgress, true, watermarkChoice);

                    const s: UploadStatus = res?.status === 'needs_date' ? 'needs_date' : 'ready';
                    results[i] = s;
                    updateItem(i, { status: s, tempId: res?.tempId, detectedDate: res?.date });
                    if (res?.analysisStatus) {
                        analysisResults.push(res as FileAnalysisResultDto);
                    }
                }
            } catch (err: any) {
                results[i] = 'error';
                updateItem(i, { status: 'error', error: err?.response?.data?.message || 'Errore' });
            }
        });
        await runConcurrent(tasks, 4);

        if (analysisResults.length > 0) {
            setSummaryResults(analysisResults);
            setSummaryIncaricoId(targetIncaricoId);
            setShowSummary(true);
            closeModal();
            setReloadKey(k => k + 1);
            return;
        }

        if (results.some(r => r === 'needs_date')) {
            setModalStep('confirm_dates');
        } else {
            const ok = results.filter(r => r === 'ready').length;
            const errors = results.filter(r => r === 'error').length;
            setCompletedSummary({ ok, errors });
            setModalStep('completed');
        }
    };

    const confirmDates = async () => {
        setIsConfirming(true);
        try {
            const toConfirm = uploadItems.filter(i => i.status === 'needs_date' && i.tempId && i.manualDate);
            await Promise.all(toConfirm.map(i => IncaricoService.confirmDate(i.tempId!, i.manualDate)));
            const ok = uploadItems.filter(i => i.status === 'ready').length + toConfirm.length;
            const errors = uploadItems.filter(i => i.status === 'error').length;
            setCompletedSummary({ ok, errors });
            setModalStep('completed');
        } catch { addToast('Errore conferma date', 'error'); }
        finally { setIsConfirming(false); }
    };

    const handleCreatePersonalFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        setIsCreatingFolder(true);
        try {
            await PersonalDriveService.createFolder(name, currentPersonalFolderId);
            setNewFolderModalOpen(false);
            setNewFolderName('');
            setReloadKey(k => k + 1);
            addToast('Cartella creata', 'success');
        } catch {
            addToast('Errore creazione cartella', 'error');
        } finally {
            setIsCreatingFolder(false);
        }
    };

    // Svuota solo le prove foto/video del mandato (S3 + DB); restano mandato,
    // incarichi, report, contabilità, documenti d'ufficio e foto km. Serve a liberare
    // spazio dopo aver scaricato le prove di un mandato chiuso.
    const handleDeleteCurrentPratica = async () => {
        if (!praticaId) return;
        if (deleteConfirmText.trim().toUpperCase() !== 'SVUOTA') return;
        if (!svuotaOpts.prove && !svuotaOpts.fotoKm && !svuotaOpts.documenti) return;
        setIsDeletingPratica(true);
        try {
            const res: any = await PraticaService.svuotaMedia(praticaId, svuotaOpts);
            const removed = res?.data ?? 0;
            addToast(
                removed > 0
                    ? `${removed} elementi eliminati dal cloud (mandato conservato)`
                    : 'Niente da eliminare per le categorie scelte',
                'success',
            );
            setShowDeletePraticaModal(false);
            setDeleteConfirmText('');
            setReloadKey(k => k + 1);
        } catch (e: any) {
            const status = e?.response?.status;
            const serverMsg = e?.response?.data?.message;
            const msg = status === 403
                ? 'Non hai i permessi per svuotare i file di questo mandato (probabilmente non è di una tua sede).'
                : (serverMsg || 'Errore durante lo svuotamento');
            addToast(msg, 'error');
        } finally {
            setIsDeletingPratica(false);
        }
    };

    // Elimina un singolo file dal Cloud Gestionale:
    // - media → IncaricoService.deleteMedia (ADMIN elimina subito, RESPONSABILE solo
    // se abilitato nelle impostazioni, altrimenti diventa una richiesta; il toast
    // riflette l'esito)
    // - altri file → FilesService.delete
    // Basta window.confirm: per un singolo file il rischio è limitato.
    const handleDeleteSingleFile = async (file: any) => {
        if (!file || file.id == null) return;
        const label = file.cleanName || file.name || `file #${file.id}`;
        if (!window.confirm(`Eliminare definitivamente "${label}" dal cloud?`)) return;
        try {
            if (file.isMedia) {
                const res: any = await IncaricoService.deleteMedia(file.id);
                const status = res?.data?.status || res?.status;
                if (status === 'created' || status === 'already_pending') {
                    addToast('Richiesta di eliminazione inviata al titolare per approvazione.', 'info');
                } else {
                    addToast('File eliminato', 'success');
                }
            } else {
                await FilesService.delete(file.id);
                addToast('File eliminato', 'success');
            }
            setReloadKey(k => k + 1);
        } catch (e: any) {
            const status = e?.response?.status;
            const serverMsg = e?.response?.data?.message;
            const msg = status === 403
                ? 'Non hai i permessi per eliminare questo file.'
                : (serverMsg || 'Errore durante l\'eliminazione del file');
            addToast(msg, 'error');
        }
    };

    const handleDeletePersonalItem = async (id: number, isFolder: boolean) => {
        const msg = isFolder
            ? 'Eliminare la cartella e tutto il contenuto?'
            : 'Eliminare questo file?';
        if (!window.confirm(msg)) return;
        try {
            await PersonalDriveService.delete(id);
            setReloadKey(k => k + 1);
            addToast('Eliminato', 'success');
        } catch {
            addToast('Errore eliminazione', 'error');
        }
    };

    // Chiavi selezionabili nella vista corrente. Nei file del mandato solo
    // ADMIN/RESPONSABILE possono selezionare; nel Drive personale l'utente è il
    // proprietario e non serve controllo sul ruolo.
    const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE;
    const selectableKeys = useMemo<string[]>(() => {
        if (viewData.type === 'FILES') {
            if (!isStaff) return [];
            return (viewData.items as any[]).map(f => (f.isMedia ? `media:${f.id}` : `office:${f.id}`));
        }
        if (viewData.type === 'PERSONAL_DRIVE') {
            const folders = ((viewData as any).folders as any[]).map(f => `pfolder:${f.id}`);
            const files = ((viewData as any).files as any[]).map(f => `pfile:${f.id}`);
            return [...folders, ...files];
        }
        return [];
    }, [viewData, isStaff]);

    const selectedCount = useMemo(
        () => selectableKeys.filter(k => selectedKeys.has(k)).length,
        [selectableKeys, selectedKeys],
    );
    const selectAllSelectable = () => setSelectedKeys(new Set(selectableKeys));

    // Eliminazione multipla: ogni chiave va al suo endpoint (media, ufficio,
    // personale) con concorrenza limitata; un solo toast con l'esito complessivo.
    const runBatchDelete = async () => {
        const keys = selectableKeys.filter(k => selectedKeys.has(k));
        if (keys.length === 0) return;
        const n = keys.length;
        const ok = window.confirm(
            `Eliminare ${n} element${n === 1 ? 'o' : 'i'} selezionat${n === 1 ? 'o' : 'i'}?\n\n`
            + `I media dei mandati per cui non hai l'eliminazione diretta andranno in richiesta di approvazione al titolare. `
            + `Le cartelle personali eliminano anche il loro contenuto. L'operazione non è reversibile.`,
        );
        if (!ok) return;
        setIsBatchDeleting(true);
        const settled = await mapWithConcurrency(keys, 4, async (key): Promise<'deleted' | 'requested'> => {
            const sep = key.indexOf(':');
            const type = key.slice(0, sep);
            const id = Number(key.slice(sep + 1));
            if (type === 'media') return classifyMediaDeleteResponse(await IncaricoService.deleteMedia(id));
            if (type === 'office') { await FilesService.delete(id); return 'deleted'; }
            await PersonalDriveService.delete(id); // pfile | pfolder
            return 'deleted';
        });
        const summary = { deleted: 0, requested: 0, errors: 0 };
        for (const r of settled) {
            if (r.status === 'rejected') summary.errors++;
            else if (r.value === 'deleted') summary.deleted++;
            else summary.requested++;
        }
        setIsBatchDeleting(false);
        addToast(summarizeBatchDelete(summary),
            summary.errors && !summary.deleted && !summary.requested ? 'error' : 'success');
        clearSelection();
        setReloadKey(k => k + 1);
    };

    const StatusIcon = ({ status }: { status: UploadStatus }) => {
        if (status === 'pending')    return <Circle size={16} className="text-slate-300" />;
        if (status === 'uploading')  return <Loader2 size={16} className="text-blue-500 animate-spin" />;
        if (status === 'ready')      return <CheckCircle2 size={16} className="text-emerald-500" />;
        if (status === 'needs_date') return <AlertTriangle size={16} className="text-amber-500" />;
        return <XCircle size={16} className="text-rose-500" />;
    };

    const FolderCard = ({ name, icon }: { name: string; icon: React.ReactNode }) => (
        <div onClick={() => navigateTo(name)} className="group bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center gap-4 cursor-pointer hover:border-blue-400 hover:shadow-lg transition-all">
            <div className="p-4 bg-slate-50 rounded-full group-hover:bg-blue-50 transition-colors">{icon}</div>
            <span className="font-bold text-slate-700 text-xs uppercase tracking-wider text-center">{name}</span>
        </div>
    );

    const FileCard = ({ file }: { file: any }) => {
        const isVid = isVideoExt(file.name);
        const isPdf = file.ext === 'pdf';
        const isDoc = (!file.isMedia && !['jpg', 'jpeg', 'png', 'webp'].includes(file.ext)) || isPdf;
        const isProcessing = file.isMedia && !file.urlCompressed && !isPdf;
        const thumbUrl = file.urlCompressed || file.urlOriginal;
        // Video con originale non riproducibile (es. .MTS non convertito): segnaposto.
        const vidNotPlayable = isVid && !isBrowserPlayableVideoUrl(thumbUrl);
        // Elimina sulla card, visibile in hover ad ADMIN/RESPONSABILE. I permessi
        // veri li verifica il BE.
        const canDeleteSingle = !file.isPersonal && (user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE);
        // I file personali sono sempre selezionabili dal proprietario, media e ufficio solo dallo staff.
        const fileKey = file.isPersonal ? `pfile:${file.id}` : file.isMedia ? `media:${file.id}` : `office:${file.id}`;
        const canSelectFile = file.isPersonal ? true : (user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE);
        const isSelectedForBatch = selectedKeys.has(fileKey);
        return (
            <div className="flex flex-col gap-2">
                <div onClick={() => openPreview(file)} className={`group relative aspect-square bg-slate-900 rounded-xl overflow-hidden border cursor-pointer shadow-sm hover:shadow-md transition-all ${isSelectedForBatch ? 'border-rose-500 ring-2 ring-rose-300' : isProcessing ? 'border-amber-400 hover:border-amber-500' : 'border-slate-200 hover:border-blue-400'}`}>
                    {canSelectFile && (
                        <label
                            onClick={e => e.stopPropagation()}
                            className={`absolute top-2 left-2 z-20 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer transition-all ${isSelectedForBatch ? 'bg-rose-600 shadow-lg' : 'bg-white/90 hover:bg-white border border-slate-300 opacity-80 hover:opacity-100'}`}
                            title={isSelectedForBatch ? 'Deseleziona' : 'Seleziona per eliminazione'}
                        >
                            <input type="checkbox" className="hidden" checked={isSelectedForBatch} onChange={() => toggleKey(fileKey)} />
                            {isSelectedForBatch && <Check size={14} className="text-white" strokeWidth={3} />}
                        </label>
                    )}
                    {canDeleteSingle && (
                        <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteSingleFile(file); }}
                            title="Elimina questo file dal cloud"
                            className="absolute top-2 right-2 z-10 p-1.5 bg-rose-600/90 hover:bg-rose-700 text-white rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <Trash2 size={14} />
                        </button>
                    )}
                    {isPdf ? (
                        <PdfThumb
                            cacheKey={`${file.isPersonal ? 'pd' : file.isMedia ? 'med' : 'off'}-${file.id}`}
                            getUrl={async () => {
                                if (file.isPersonal) {
                                    return (await PersonalDriveService.getDownloadUrl(file.id)) || file.urlOriginal;
                                }
                                const res = await (file.isMedia
                                    ? IncaricoService.getMediaUrl(file.id)
                                    : FilesService.getDownloadUrl(file.id));
                                return extractUrl(res) || file.urlCompressed || file.urlOriginal;
                            }}
                        />
                    ) : isDoc
                        ? <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 gap-1"><FileText size={36} className="text-slate-400" /></div>
                        : isVid
                            ? (vidNotPlayable
                                ? <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-slate-800 text-slate-300"><Video size={30} className="opacity-70" /><span className="text-[9px] font-black uppercase tracking-wider">.{file.ext || 'video'}</span></div>
                                : <div className="w-full h-full flex items-center justify-center relative"><video src={thumbUrl} preload="metadata" className="w-full h-full object-cover opacity-60" muted /><PlayCircle className="text-white opacity-80 w-10 h-10 absolute" /></div>)
                            : <img src={thumbUrl} alt={file.cleanName} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" loading="lazy" />
                    }
                    <div className={`absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent transition-opacity p-3 flex flex-col justify-end ${(isPdf || isDoc) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {/* Documenti ufficio: nome sempre visibile, la miniatura non basta a riconoscerli. */}
                        <p className="text-[10px] text-white font-bold truncate" title={file.cleanName}>{file.cleanName}</p>
                    </div>
                    {isProcessing && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm text-white text-[9px] font-black px-2 py-1 rounded-lg pointer-events-none">
                            <Loader2 size={9} className="animate-spin shrink-0" /> ELABORAZIONE
                        </div>
                    )}
                </div>
                <div className="px-1">
                    <p className="text-[10px] font-black text-slate-700 uppercase leading-none">{file.dateObj.toLocaleDateString('it-IT')}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase truncate">{file.uploaderName}</p>
                </div>
            </div>
        );
    };

    const canDownloadZip = praticaId && (downloadableItems.mediaIds.length + downloadableItems.fileIds.length > 0);

    // Documento dei collaboratori: PDF generato dal server, che registra il download.
    const scaricaDocumentoCollab = async (id: number) => {
        if (documentoInCorso) return;
        setDocumentoInCorso(id);
        try {
            const pdf = await DocUfficioService.pdfCollaboratori(id);
            if (!pdf?.url) throw new Error('url mancante');
            downloadAttachmentUrl(pdf.url);
        } catch {
            addToast('Documento non generato, riprova', 'error');
        } finally {
            setDocumentoInCorso(null);
        }
    };

    // Le impostazioni dell'agenzia (immagini comprese) si preparano una volta per visita.
    const scaricaModulo = async (tipo: ModuloInBianco, file: string) => {
        if (moduloInCorso) return;
        setModuloInCorso(tipo);
        try {
            if (!impostazioniModuliRef.current) {
                const res: any = await SettingsService.getAll();
                impostazioniModuliRef.current = await impostazioniPerPdf(res?.data?.[0] || {});
            }
            const blob = await generaModuloInBianco(tipo, impostazioniModuliRef.current);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            DocUfficioService.moduloScaricato(tipo).catch(() => { /* il registro non blocca il download */ });
        } catch (e) {
            console.warn('[FileManager] modulo in bianco non generato:', e);
            addToast('Modulo non generato, riprova', 'error');
        } finally {
            setModuloInCorso(null);
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-[1600px] mx-auto">
            {/* Header */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-600 p-2.5 rounded-xl text-white"><HardDrive size={20} /></div>
                        <h1 className="text-lg font-black text-slate-900 uppercase tracking-wide">Cloud Gestionale</h1>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input type="text" placeholder="Cerca..." className="pl-9 pr-4 py-2 border border-slate-200 bg-slate-50 rounded-xl w-56 text-sm outline-none focus:ring-2 focus:ring-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                        {pathSegments.map((seg, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <ChevronRight size={14} className="text-slate-300 shrink-0" />}
                                <button onClick={() => navigateBackTo(i)} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${i === pathSegments.length - 1 ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                                    {getBreadcrumbLabel(seg)}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex gap-2 shrink-0">
                        {canDownloadZip && !isPersonalDrive && (
                            <button onClick={handleOpenZipModal} disabled={isGeneratingZip}
                                className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md disabled:opacity-60">
                                {isGeneratingZip ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}
                                Scarica ZIP
                            </button>
                        )}
                        {/*
                          Svuota media del mandato, per titolare e responsabili (accesso alla sede
                          verificato in PraticaService.checkAccess). È accanto a "Scarica ZIP" perché
                          di solito si scaricano gli originali e poi si libera spazio. Cancella solo
                          le prove foto/video; l'eliminazione del mandato è altrove.
                        */}
                        {praticaId && !isPersonalDrive && (user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE) && (
                            <button
                                onClick={openSvuotaModal}
                                className="bg-rose-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md hover:bg-rose-700 transition-colors"
                                title="Svuota i file di questo mandato dal cloud, scegliendo cosa (il mandato resta)"
                            >
                                <Trash2 size={16} /> Svuota media
                            </button>
                        )}
                        {isPersonalDrive && (
                            <button onClick={() => { setNewFolderName(''); setNewFolderModalOpen(true); }}
                                className="bg-slate-700 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md">
                                <FolderPlus size={16} /> Nuova cartella
                            </button>
                        )}
                        {!isPersonalDrive && recoveryItems.length > 0 && (
                            <button onClick={() => setRecoveryOpen(true)}
                                title="File caricati ma non ancora visibili (senza data o duplicati): completali qui"
                                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md">
                                <AlertTriangle size={16} /> Da completare ({recoveryItems.length})
                            </button>
                        )}
                        <button onClick={() => setIsUploadModalOpen(true)} disabled={!praticaId && !isPersonalDrive}
                            className="bg-blue-600 disabled:opacity-40 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md">
                            <UploadCloud size={16} /> Carica
                        </button>
                    </div>
                </div>
            </div>

            {/* Contenuto */}
            <div className="min-h-[400px]">
                {isLoading ? (
                    <div className="flex items-center justify-center h-64 text-blue-500 gap-3"><Loader2 className="animate-spin" size={36} /><b>Caricamento...</b></div>
                ) : (
                    <>
                        {/* Toolbar selezione multipla, nelle viste con file selezionabili. */}
                        {selectableKeys.length > 0 && (
                            <div className="mb-4 bg-rose-50 border border-rose-200 rounded-2xl shadow-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3 sticky top-2 z-20">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={selectAllSelectable}
                                        disabled={isBatchDeleting}
                                        className="px-3 py-1.5 text-xs font-bold text-rose-700 bg-white border border-rose-200 hover:bg-rose-100 rounded-lg disabled:opacity-40 flex items-center gap-1.5"
                                    >
                                        <CheckCircle2 size={13} /> Seleziona tutto ({selectableKeys.length})
                                    </button>
                                    <span className="text-sm font-bold text-rose-800">
                                        {selectedCount} selezionat{selectedCount === 1 ? 'o' : 'i'}
                                    </span>
                                    {selectedCount > 0 && (
                                        <button
                                            onClick={clearSelection}
                                            disabled={isBatchDeleting}
                                            className="px-2 py-1.5 text-xs font-bold text-slate-600 hover:bg-white rounded-lg disabled:opacity-30"
                                        >
                                            Annulla
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={runBatchDelete}
                                    disabled={isBatchDeleting || selectedCount === 0}
                                    className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {isBatchDeleting
                                        ? <><Loader2 size={14} className="animate-spin" /> Elimino…</>
                                        : <><Trash2 size={14} /> Elimina ({selectedCount})</>}
                                </button>
                            </div>
                        )}

                        {viewData.type === 'ROOT' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                {(user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE) && (
                                <div onClick={() => navigateTo('MODULI')}
                                    className="group bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-5 flex flex-col items-center gap-3 cursor-pointer hover:border-indigo-400 hover:shadow-lg transition-all">
                                    <div className="bg-indigo-100 p-4 rounded-full group-hover:bg-indigo-200 mt-2">
                                        <Briefcase className="text-indigo-600" size={28} />
                                    </div>
                                    <span className="font-black text-indigo-700 text-[11px] text-center uppercase tracking-tight">Doc. Ufficio</span>
                                </div>
                                )}
                                {/* Drive personale solo per il Titolare (PersonalDriveController è ADMIN-only). */}
                                {user?.role === UserRole.ADMIN && (
                                <div onClick={() => navigateTo('PERSONAL')}
                                    className="group bg-gradient-to-br from-sky-50 to-cyan-100 border-2 border-sky-200 rounded-2xl p-5 flex flex-col items-center gap-3 cursor-pointer hover:border-sky-400 hover:shadow-lg transition-all relative">
                                    <span className="absolute top-2 left-2 bg-sky-700 text-white text-[9px] font-black px-2 py-0.5 rounded-md">MIO</span>
                                    <div className="bg-white p-4 rounded-full group-hover:bg-sky-50 mt-2 shadow-sm">
                                        <Cloud className="text-sky-600 fill-sky-100" size={28} />
                                    </div>
                                    <span className="font-black text-sky-900 text-[11px] text-center uppercase tracking-tight">Drive Personale</span>
                                </div>
                                )}
                                {pratiche.map(p => (
                                    <div key={p.id} onClick={() => navigateTo(`PRATICA_${p.id}`)}
                                        className="group bg-white border border-slate-200 rounded-2xl p-5 flex flex-col items-center gap-3 cursor-pointer hover:border-indigo-400 hover:shadow-lg transition-all relative">
                                        <span className="absolute top-2 left-2 bg-slate-900 text-white text-[9px] font-black px-2 py-0.5 rounded-md">#{p.id}</span>
                                        <div className="bg-indigo-50 p-4 rounded-full group-hover:bg-indigo-100 mt-2">
                                            <Folder className="text-indigo-500 fill-indigo-200" size={28} />
                                        </div>
                                        <span className="font-bold text-slate-700 text-[11px] text-center line-clamp-2 uppercase tracking-tight">{p.name || `Mandato #${p.id}`}</span>
                                    </div>
                                ))}
                                {pratiche.length === 0 && (
                                    <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                        <FolderOpen size={48} className="mx-auto mb-4 opacity-20" />Nessuna pratica
                                    </div>
                                )}
                                {/* Carica altri, nascosto durante la ricerca. */}
                                {fmHasMore && !searchQuery.trim() && (
                                    <div className="col-span-full flex justify-center pt-2">
                                        <button onClick={() => loadAll(fmPage + 1, true)} disabled={fmLoadingMore}
                                            className="px-5 py-2.5 rounded-xl bg-indigo-50 text-indigo-700 font-bold text-sm hover:bg-indigo-100 disabled:opacity-50">
                                            {fmLoadingMore ? 'Caricamento…' : 'Carica altri mandati'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {viewData.type === 'MODULI' && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-500">
                                    Moduli in bianco con i dati dell'agenzia, da stampare e compilare a mano.
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                    <div onClick={() => navigateTo('COLLABORATORI')}
                                        className="group bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-5 flex flex-col items-center gap-3 cursor-pointer hover:border-indigo-400 hover:shadow-lg transition-all">
                                        <div className="bg-indigo-100 p-4 rounded-full group-hover:bg-indigo-200">
                                            <Users className="text-indigo-600" size={28} />
                                        </div>
                                        <span className="font-black text-indigo-700 text-[11px] text-center uppercase tracking-tight">Collaboratori</span>
                                    </div>
                                    {MODULI_IN_BIANCO.map(m => (
                                        <button key={m.tipo} type="button" disabled={!!moduloInCorso}
                                            onClick={() => scaricaModulo(m.tipo, m.file)}
                                            className="group bg-white border border-slate-200 rounded-2xl p-5 flex flex-col items-center gap-3 hover:border-indigo-400 hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-wait">
                                            <div className="bg-red-50 p-4 rounded-full group-hover:bg-red-100">
                                                {moduloInCorso === m.tipo
                                                    ? <Loader2 className="text-red-500 animate-spin" size={28} />
                                                    : <FileText className="text-red-500" size={28} />}
                                            </div>
                                            <span className="font-bold text-slate-700 text-[11px] text-center uppercase tracking-tight">{m.nome}</span>
                                            <span className="text-[10px] text-slate-400 flex items-center gap-1"><Download size={11} /> PDF</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {viewData.type === 'COLLABORATORI' && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-500">
                                    Ultima versione dei documenti che i collaboratori leggono e accettano, da stampare.
                                </p>
                                {documentiCollab === null ? (
                                    <div className="flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> Caricamento…</div>
                                ) : documentiCollab.length === 0 ? (
                                    <div className="py-16 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                        <FolderOpen size={40} className="mx-auto mb-3 opacity-20" />Nessun documento pubblicato
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                        {documentiCollab.map(d => (
                                            <button key={d.id} type="button" disabled={!!documentoInCorso}
                                                onClick={() => scaricaDocumentoCollab(d.id)}
                                                className="group bg-white border border-slate-200 rounded-2xl p-5 flex flex-col items-center gap-3 hover:border-indigo-400 hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-wait">
                                                <div className="bg-red-50 p-4 rounded-full group-hover:bg-red-100">
                                                    {documentoInCorso === d.id
                                                        ? <Loader2 className="text-red-500 animate-spin" size={28} />
                                                        : <FileText className="text-red-500" size={28} />}
                                                </div>
                                                <span className="font-bold text-slate-700 text-[11px] text-center uppercase tracking-tight">{d.titolo}</span>
                                                <span className="text-[10px] text-slate-400">
                                                    Versione {d.versione}{d.inVigoreDal ? ` · dal ${d.inVigoreDal}` : ''}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {viewData.type === 'FIRST_LEVEL' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                {(viewData as any).hasUfficio && (
                                    <div onClick={() => navigateTo('DOC. UFFICIO')} className="group bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6 flex flex-col items-center gap-4 cursor-pointer hover:border-indigo-400 hover:shadow-lg transition-all">
                                        <div className="p-4 bg-indigo-100 rounded-full group-hover:bg-indigo-200"><Briefcase className="text-indigo-600" size={24} /></div>
                                        <span className="font-bold text-indigo-700 text-xs uppercase tracking-wider text-center">Documentazione Ufficio</span>
                                    </div>
                                )}
                                {(viewData as any).years.map((yr: string) => (
                                    <FolderCard key={yr} name={yr} icon={<Layers className="text-indigo-500" size={28} />} />
                                ))}
                                {!(viewData as any).hasUfficio && (viewData as any).years.length === 0 && (
                                    <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                        <FolderOpen size={48} className="mx-auto mb-4 opacity-20" />Nessun media
                                    </div>
                                )}
                            </div>
                        )}

                        {viewData.type === 'FOLDERS' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                {(viewData.items as string[]).map(item => (
                                    <FolderCard key={item} name={item} icon={viewData.icon} />
                                ))}
                                {(viewData.items as string[]).length === 0 && (
                                    <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                        <FolderOpen size={48} className="mx-auto mb-4 opacity-20" />Cartella vuota
                                    </div>
                                )}
                            </div>
                        )}

                        {viewData.type === 'FILES' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                                {(viewData.items as any[]).map(f => <FileCard key={`${f.isMedia ? 'm' : 'f'}-${f.id}`} file={f} />)}
                                {(viewData.items as any[]).length === 0 && (
                                    <div className="col-span-full py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                        <FolderOpen size={48} className="mx-auto mb-4 opacity-20" />Nessun file
                                    </div>
                                )}
                            </div>
                        )}

                        {viewData.type === 'PERSONAL_DRIVE' && (
                            isPersonalLoading ? (
                                <div className="flex items-center justify-center h-64 text-sky-500 gap-3"><Loader2 className="animate-spin" size={36} /><b>Caricamento Drive...</b></div>
                            ) : (
                                <div className="space-y-8">
                                    {(viewData as any).folders.length > 0 && (
                                        <div>
                                            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-3">Cartelle</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                                {(viewData as any).folders.map((f: any) => {
                                                    const folderKey = `pfolder:${f.id}`;
                                                    const isSelFolder = selectedKeys.has(folderKey);
                                                    return (
                                                    <div key={`pf-${f.id}`} className={`group relative bg-white border rounded-2xl p-5 flex flex-col items-center gap-3 cursor-pointer hover:shadow-lg transition-all ${isSelFolder ? 'border-rose-400 ring-2 ring-rose-200' : 'border-slate-200 hover:border-sky-400'}`}
                                                        onClick={() => navigateTo(`PD_${f.id}`)}>
                                                        <label
                                                            onClick={e => e.stopPropagation()}
                                                            className={`absolute top-2 left-2 z-20 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer transition-all ${isSelFolder ? 'bg-rose-600 shadow-lg' : 'bg-white/90 hover:bg-white border border-slate-300 opacity-80 hover:opacity-100'}`}
                                                            title={isSelFolder ? 'Deseleziona' : 'Seleziona per eliminazione'}
                                                        >
                                                            <input type="checkbox" className="hidden" checked={isSelFolder} onChange={() => toggleKey(folderKey)} />
                                                            {isSelFolder && <Check size={14} className="text-white" strokeWidth={3} />}
                                                        </label>
                                                        <button onClick={(e) => { e.stopPropagation(); handleDeletePersonalItem(f.id, true); }}
                                                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/0 hover:bg-rose-50 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all">
                                                            <Trash2 size={14} />
                                                        </button>
                                                        <div className="p-3 bg-sky-50 rounded-full group-hover:bg-sky-100">
                                                            <Folder className="text-sky-500 fill-sky-200" size={26} />
                                                        </div>
                                                        <span className="font-bold text-slate-700 text-[11px] text-center line-clamp-2 uppercase tracking-tight">{f.name}</span>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {(viewData as any).files.length > 0 && (
                                        <div>
                                            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-3">File</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                                                {(viewData as any).files.map((f: any) => (
                                                    <div key={`pfile-${f.id}`} className="relative group">
                                                        <button onClick={(e) => { e.stopPropagation(); handleDeletePersonalItem(f.id, false); }}
                                                            className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-white/90 hover:bg-rose-50 text-slate-500 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all shadow-sm">
                                                            <Trash2 size={14} />
                                                        </button>
                                                        <FileCard file={f} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {(viewData as any).folders.length === 0 && (viewData as any).files.length === 0 && (
                                        <div className="py-20 text-center text-slate-400 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                            <Cloud size={48} className="mx-auto mb-4 opacity-20" />
                                            <p className="font-bold text-sm">Drive vuoto</p>
                                            <p className="text-xs mt-1">Carica file o crea una cartella per iniziare</p>
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </>
                )}
            </div>

            {/* Modale riepilogo upload */}
            <UploadSummaryModal
                isOpen={showSummary}
                onClose={() => { setShowSummary(false); setReloadKey(k => k + 1); }}
                results={summaryResults}
                incaricoId={summaryIncaricoId}
                context="admin"
                onPollStatus={IncaricoService.getMediaAnalysisStatus}
                onManualDateSave={async (mediaId, tempId, date, time) => {
                    const iso = `${date}T${time}:00`;
                    await IncaricoService.confirmDate(tempId, iso);
                    addToast('Data salvata', 'success');
                }}
                onDiscard={async (_mediaId, tempId) => {
                    await IncaricoService.deleteMediaByTempId(tempId);
                    addToast('File annullato', 'info');
                }}
                onKeep={async (mediaId) => {
                    await IncaricoService.keepMedia(mediaId);
                    addToast('File mantenuto', 'success');
                }}
            />

            {/*
              Recupero media nascosti: stessa UI del riepilogo ma su media già salvati
              (per mediaId). Si imposta la data o si tengono/eliminano i duplicati, senza approvazione.
            */}
            <UploadSummaryModal
                isOpen={recoveryOpen}
                onClose={() => { setRecoveryOpen(false); setReloadKey(k => k + 1); }}
                results={recoveryItems}
                incaricoId={0}
                context="admin"
                onManualDateSave={async (mediaId, _tempId, date, time) => {
                    const iso = `${date}T${time}:00`;
                    await IncaricoService.updateMediaDate(mediaId, iso);
                    addToast('Data salvata', 'success');
                }}
                onDiscard={async (mediaId) => {
                    await IncaricoService.deleteMedia(mediaId);
                    addToast('File eliminato', 'info');
                }}
                onKeep={async (mediaId) => {
                    await IncaricoService.keepMedia(mediaId);
                    addToast('File mantenuto', 'success');
                }}
            />

            {/* Modale nuova cartella */}
            {newFolderModalOpen && (
                <Modal title="Nuova cartella" onClose={() => setNewFolderModalOpen(false)}>
                    <div className="space-y-4">
                        <label className="block">
                            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Nome</span>
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={e => setNewFolderName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleCreatePersonalFolder(); }}
                                autoFocus
                                placeholder="Es. Documenti, Foto vacanza..."
                                className="mt-1.5 w-full text-sm p-2.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-sky-500 outline-none"
                            />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setNewFolderModalOpen(false)} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Annulla</button>
                            <button onClick={handleCreatePersonalFolder} disabled={!newFolderName.trim() || isCreatingFolder}
                                className="bg-sky-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50 shadow-md">
                                {isCreatingFolder ? <Loader2 className="animate-spin" size={18} /> : <FolderPlus size={18} />}
                                Crea
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modale svuota media */}
            {showDeletePraticaModal && praticaId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4 text-rose-600">
                                <div className="p-3 bg-rose-100 rounded-full">
                                    <AlertTriangle size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900">Svuota media del mandato</h3>
                            </div>
                            <p className="text-slate-600 mb-3 font-medium">
                                Scegli cosa eliminare dal cloud di <strong className="text-slate-900">{praticaName}</strong> per liberare spazio. Il <strong>mandato resta</strong> (incarichi, report e contabilità non vengono toccati).
                            </p>

                            {/* Categorie selezionabili con conteggio. */}
                            <div className="space-y-2">
                                {([
                                    { key: 'prove' as const, titolo: 'Prove foto/video', sub: 'Le foto e i video delle indagini' },
                                    { key: 'fotoKm' as const, titolo: 'Foto contachilometri', sub: 'Km iniziali / finali dei collaboratori' },
                                    { key: 'documenti' as const, titolo: 'Documenti d\'ufficio', sub: 'PDF firmato, audit e foto documento d\'identità (riconoscimento)' },
                                ]).map(cat => {
                                    const count = svuotaCounts ? svuotaCounts[cat.key] : null;
                                    const empty = count === 0;
                                    return (
                                        <label
                                            key={cat.key}
                                            className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${empty ? 'opacity-50 cursor-not-allowed border-slate-200' : 'cursor-pointer border-slate-200 hover:bg-slate-50'} ${svuotaOpts[cat.key] && !empty ? 'border-rose-300 bg-rose-50' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
                                                checked={svuotaOpts[cat.key] && !empty}
                                                disabled={empty}
                                                onChange={e => setSvuotaOpts(o => ({ ...o, [cat.key]: e.target.checked }))}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-bold text-slate-800 flex items-center justify-between gap-2">
                                                    <span>{cat.titolo}</span>
                                                    <span className="text-xs font-mono text-slate-500">{count === null ? '…' : `${count} file`}</span>
                                                </div>
                                                <div className="text-xs text-slate-500 mt-0.5">{cat.sub}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>

                            <div className="bg-rose-50 p-3 rounded-xl border border-rose-100 mt-3 text-xs text-rose-800 leading-relaxed">
                                <strong>Azione irreversibile.</strong> I file delle categorie scelte vengono rimossi da cloud e database (originale + versione compressa). Se non hai ancora scaricato lo ZIP, annulla e fallo prima. I documenti d'identità del riconoscimento sono prova legale: eliminali solo se ne hai una copia.
                            </div>
                            <div className="mt-4">
                                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                                    Per confermare digita <span className="text-rose-600 font-mono">SVUOTA</span>
                                </label>
                                <input
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={e => setDeleteConfirmText(e.target.value)}
                                    placeholder="SVUOTA"
                                    autoFocus
                                    className="w-full px-4 py-2.5 border-2 border-rose-200 rounded-xl text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-rose-300"
                                />
                            </div>
                        </div>
                        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-100">
                            <button
                                onClick={() => { setShowDeletePraticaModal(false); setDeleteConfirmText(''); }}
                                disabled={isDeletingPratica}
                                className="px-5 py-2.5 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 font-bold transition-colors disabled:opacity-50 shadow-sm"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleDeleteCurrentPratica}
                                disabled={isDeletingPratica || deleteConfirmText.trim().toUpperCase() !== 'SVUOTA' || (!svuotaOpts.prove && !svuotaOpts.fotoKm && !svuotaOpts.documenti)}
                                className="px-5 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 font-bold flex items-center gap-2 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
                            >
                                {isDeletingPratica ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}
                                {isDeletingPratica ? 'Svuotamento…' : 'Svuota media'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale ZIP */}
            {zipModalOpen && (
                <Modal title="Scarica Archivio ZIP" onClose={() => setZipModalOpen(false)}>
                    <div className="space-y-4">
                        {/* Step 1: scelta del formato (originali o con watermark), con due card ben distinte. */}
                        {zipOriginals === null && !isGeneratingZip && (
                            <>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                                    <p className="font-bold mb-1 flex items-center gap-1.5"><AlertTriangle size={14}/> Scegli quale versione dei file vuoi nello ZIP</p>
                                    <p className="opacity-80 leading-snug">Per uso legale/probatorio servono gli <b>originali</b> (come caricati dal collaboratore). Per archivio gestionale conviene la versione <b>con watermark e timestamp</b>.</p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleConfirmZipChoice(true)}
                                    className="w-full text-left bg-white border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/40 transition-all rounded-xl p-4 flex items-start gap-3 group"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                        <FileCheck size={22} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-slate-900">Originali</p>
                                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">File <b>esattamente come caricati</b> dal collaboratore. Nessun watermark, nessun timestamp impresso, EXIF intatti. Consigliato per uso <b>legale/probatorio</b> e per consegna al cliente prima di eliminare il mandato.</p>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleConfirmZipChoice(false)}
                                    className="w-full text-left bg-white border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 transition-all rounded-xl p-4 flex items-start gap-3 group"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                        <ShieldCheck size={22} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-slate-900">Versione gestionale (con watermark/timestamp)</p>
                                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">Stessa versione che vedi nei <b>Media</b>: con eventuale watermark e timestamp impresso sulla foto. Più leggera dell'originale. Adatta per condivisione interna o archivio rapido.</p>
                                    </div>
                                </button>
                            </>
                        )}

                        {/* Step 1.5: generazione piano in corso */}
                        {isGeneratingZip && (
                            <div className="flex flex-col items-center justify-center py-8 gap-3">
                                <Loader2 className="animate-spin text-blue-500" size={32} />
                                <p className="text-xs text-slate-500">Sto preparando lo ZIP {zipOriginals ? 'con file originali' : 'con timestamp'}...</p>
                            </div>
                        )}

                        {/* Step 2: piano pronto, elenco delle parti */}
                        {zipPlan && !isGeneratingZip && (
                            <>
                                <div className={`rounded-xl p-3 border text-sm flex items-center gap-2 ${zipOriginals ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                                    {zipOriginals ? <FileCheck size={18}/> : <ShieldCheck size={18}/>}
                                    <span className="font-bold">{zipOriginals ? 'ORIGINALI' : 'CON TIMESTAMP'}</span>
                                    <span className="opacity-80">·</span>
                                    <span className="opacity-80"><b>{zipPlan.totalFiles}</b> file · <b>{zipPlan.totalParts}</b> {zipPlan.totalParts === 1 ? 'parte' : 'parti'} da ~50 file</span>
                                </div>
                                {/* Archivio completo (un solo ZIP) sopra le parti. */}
                                {zipPlan.fullToken && (
                                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <Package size={20} className="text-emerald-700 shrink-0" />
                                            <div>
                                                <p className="text-sm font-extrabold text-emerald-900">Archivio completo</p>
                                                <p className="text-[11px] text-emerald-700">Tutti i {zipPlan.totalFiles} file in un unico ZIP</p>
                                            </div>
                                        </div>
                                        <a href={ArchivioService.getDownloadUrl(zipPlan.fullToken)} download
                                            className="bg-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-600 transition-colors">
                                            <Download size={14} /> Scarica tutto
                                        </a>
                                    </div>
                                )}
                                {zipPlan.fullToken && zipPlan.totalParts > 1 && (
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pt-1">Oppure scarica a parti</p>
                                )}
                                <div className="space-y-2 max-h-72 overflow-y-auto">
                                    {/* Con una sola parte basta l'archivio completo. */}
                                    {(zipPlan.fullToken && zipPlan.totalParts === 1 ? [] : zipPlan.parts).map(part => (
                                        <div key={part.token} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <Package size={18} className="text-emerald-600 shrink-0" />
                                                <div>
                                                    <p className="text-sm font-bold text-slate-800">Parte {part.partNumber}</p>
                                                    <p className="text-[11px] text-slate-400">{part.fileCount} file</p>
                                                </div>
                                            </div>
                                            <a href={ArchivioService.getDownloadUrl(part.token)} download
                                                className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-500 transition-colors">
                                                <Download size={14} /> Scarica
                                            </a>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => { setZipPlan(null); setZipOriginals(null); }}
                                        className="text-xs text-slate-500 hover:text-slate-800 font-semibold underline-offset-2 hover:underline"
                                    >
                                        ← Cambia versione
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {/* Anteprima */}
            {/*
              Il click sull'overlay chiude; il contenuto usa stopPropagation. Le immagini
              heic/heif/bmp/gif sono supportate perché il BE genera comunque un jpg
              compresso per previewUrl.
            */}
            {previewFile && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/95 backdrop-blur-md"
                    onClick={() => setPreviewFile(null)}
                >
                    <div
                        className="bg-white rounded-[2rem] w-full max-w-5xl flex flex-col md:grid md:grid-cols-[1fr,280px] overflow-hidden shadow-2xl h-[85vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex-1 min-h-0 min-w-0 overflow-hidden bg-slate-100 flex items-center justify-center relative p-6">
                            {isPreviewLoading ? <Loader2 className="animate-spin text-blue-600" size={48} /> : (
                                isVideoExt(previewFile.name)
                                    ? ((previewVideoError || !isBrowserPlayableVideoUrl(previewUrl))
                                        // Originale non riproducibile (es. .MTS) e MP4 convertito non ancora pronto:
                                        // messaggio invece di un player nero.
                                        ? <div className="max-w-md text-center flex flex-col items-center gap-3 px-6">
                                            <Video size={48} className="text-amber-500" />
                                            <p className="font-black text-slate-700 text-sm">Anteprima video non disponibile</p>
                                            <p className="text-xs text-slate-500 leading-relaxed">
                                                {previewVideoError
                                                    ? 'Il browser non riesce a riprodurre questo video.'
                                                    : `Il formato originale (.${(previewFile.ext || '').toUpperCase()}) non è riproducibile nel browser e la versione convertita (MP4) non è ancora pronta.`}
                                                {' '}Riprova tra qualche minuto oppure scarica l'originale.
                                            </p>
                                        </div>
                                        : <video src={previewUrl!} controls autoPlay onError={() => setPreviewVideoError(true)} className="max-w-full max-h-full rounded-xl bg-black" />)
                                    : previewFile.ext === 'pdf'
                                        ? <iframe src={`${previewUrl}#toolbar=0`} className="w-full h-full rounded-xl border-0" title="pdf" />
                                        : isPreviewableImage(previewFile.ext, previewUrl)
                                            ? <img src={previewUrl!} className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" alt="prev" />
                                            : isConvertibleImageExt(previewFile.ext)
                                                // RAW/HEIC senza copia JPG pronta: il browser non apre l'originale.
                                                ? <div className="max-w-md text-center flex flex-col items-center gap-3 px-6">
                                                    <ImageIcon size={48} className="text-amber-500" />
                                                    <p className="font-black text-slate-700 text-sm">Anteprima non ancora pronta</p>
                                                    <p className="text-xs text-slate-500 leading-relaxed">
                                                        {`Il browser non apre il formato .${(previewFile.ext || '').toUpperCase()} e la copia per l'anteprima non è ancora pronta.`}
                                                        {' '}Riprova tra qualche minuto oppure scarica il file.
                                                    </p>
                                                </div>
                                                : <div className="text-center"><FileText size={64} className="mx-auto mb-4 text-slate-300" /><p className="font-black text-slate-400 text-sm">Anteprima non disponibile</p></div>
                            )}
                        </div>
                        <div className="bg-white border-l border-slate-100 flex flex-col p-6 overflow-y-auto">
                            <div className="flex justify-between items-start mb-6">
                                <div className="p-2.5 bg-blue-50 rounded-xl"><ImageIcon size={24} className="text-blue-600" /></div>
                                <button onClick={() => setPreviewFile(null)} className="p-2 text-slate-400 hover:text-slate-900 rounded-full"><X size={20} /></button>
                            </div>
                            <h3 className="font-black text-base text-slate-900 break-words uppercase leading-tight mb-4">{previewFile.cleanName}</h3>
                            <div className="space-y-3 text-xs font-bold text-slate-500 border-y border-slate-50 py-4">
                                <div className="flex justify-between"><span>DATA</span><span className="text-slate-900">{previewFile.dateObj.toLocaleDateString('it-IT')}</span></div>
                                <div className="flex justify-between"><span>ORA</span><span className="text-slate-900">{previewFile.dateObj.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span></div>
                                <div className="flex justify-between"><span>UPLOADER</span><span className="text-slate-900 text-right max-w-[140px] truncate">{previewFile.uploaderName}</span></div>
                                <div className="flex justify-between"><span>TIPO</span><span className="text-slate-900 uppercase">{previewFile.ext}</span></div>
                                {previewFile.isMedia && !previewFile.urlCompressed && (
                                    <div className="flex items-center gap-1.5 text-amber-500 pt-1">
                                        <Loader2 size={11} className="animate-spin shrink-0" />
                                        <span className="text-[10px] font-black leading-tight">Elaborazione in corso<br /><span className="font-medium text-amber-400">Stai vedendo l'originale</span></span>
                                    </div>
                                )}
                            </div>
                            <button type="button" onClick={() => handleDownloadPreview(previewFile)} className="mt-auto bg-blue-600 text-white w-full py-3 rounded-2xl font-black text-sm flex items-center justify-center gap-2">
                                <Download size={18} /> Scarica
                            </button>
                            {previewFile.isMedia && !previewFile.isPersonal && copiaInAltroFormato(previewFile.name, previewUrl) && (
                                // "Scarica" dà la copia convertita (es. JPG di un .CR2): qui il file caricato.
                                <button type="button" onClick={async () => {
                                    try {
                                        const dl = await IncaricoService.getMediaUrl(previewFile.id, true, true);
                                        if (dl) downloadAttachmentUrl(dl);
                                    } catch { /* nessun link: niente da scaricare */ }
                                }} className="mt-2 bg-slate-100 text-slate-700 w-full py-3 rounded-2xl font-black text-sm flex items-center justify-center gap-2">
                                    <Download size={18} /> Scarica originale
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modale upload */}
            {isUploadModalOpen && (
                <Modal title={
                        modalStep === 'completed' ? 'Caricamento completato'
                        : modalStep === 'uploading' ? `Caricamento... ${uploadProgress}%`
                        : modalStep === 'confirm_dates' ? 'Date mancanti'
                        : 'Carica File'
                    }
                    onClose={modalStep === 'uploading' ? undefined : (modalStep === 'completed' ? closeCompleted : closeModal)}>

                    {modalStep === 'select' && (
                        <div className="space-y-5">
                            {isPersonalDrive && (
                                <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 flex items-center gap-3">
                                    <Cloud size={20} className="text-sky-600 shrink-0" />
                                    <p className="text-sm text-sky-900">
                                        Upload diretto nel <b>Drive Personale</b>. Nessuna compressione né analisi.
                                    </p>
                                </div>
                            )}
                            {!isPersonalDrive && (
                            <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
                                {(['ufficio', 'media'] as const).map(t => (
                                    <button key={t} onClick={() => setUploadType(t)}
                                        className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${uploadType === t ? 'bg-white shadow text-blue-700' : 'text-slate-500'}`}>
                                        {t === 'ufficio' ? <><Briefcase size={16} /> Documentazione Ufficio</> : <><Camera size={16} /> Media</>}
                                    </button>
                                ))}
                            </div>
                            )}
                            {!isPersonalDrive && uploadType === 'media' && soggettiList.length > 1 && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                                        Soggetto investigato <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={uploadSoggetto}
                                        onChange={e => setUploadSoggetto(e.target.value)}
                                        className="w-full text-sm p-2.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">— Seleziona soggetto —</option>
                                        {soggettiList.map(s => (
                                            <option key={s.nome} value={s.nome}>{s.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {!isPersonalDrive && uploadType === 'media' && (
                                <div className="grid grid-cols-5 gap-2">
                                    {DEVICE_OPTIONS.map(opt => (
                                        <button key={opt} onClick={() => setUploadCategoria(opt)}
                                            className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-[10px] font-black uppercase transition-all ${uploadCategoria === opt ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500'}`}>
                                            <Camera size={18} />{opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <label className="border-2 border-dashed border-blue-200 rounded-2xl p-6 text-center bg-blue-50/50 cursor-pointer flex flex-col items-center gap-2 hover:bg-blue-50">
                                <UploadCloud className="text-blue-400" size={28} />
                                <span className="text-xs font-bold text-slate-600">Trascina o clicca per selezionare</span>
                                {uploadItems.length > 0 && <span className="text-xs font-black text-blue-600">{uploadItems.length} file selezionati</span>}
                                <input type="file" multiple className="hidden" onChange={e => {
                                    setUploadItems(Array.from(e.target.files || []).map(f => ({ file: f, status: 'pending', manualDate: '' })));
                                }} />
                            </label>

                            {/*
                              Scelta watermark, solo per i media degli incarichi: Drive personale e
                              Doc Ufficio non passano dalla pipeline del watermark. Default attivo.
                            */}
                            {!isPersonalDrive && uploadType === 'media' && (
                                <label className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={applyDateWatermark}
                                        onChange={e => setApplyDateWatermark(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-black text-slate-700 uppercase tracking-tight">Applica data come watermark</p>
                                        <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                                            Stampa la data di acquisizione (EXIF) sull'immagine. Disattiva se la foto la contiene già.
                                        </p>
                                    </div>
                                </label>
                            )}

                            <div className="flex justify-end gap-2">
                                <button onClick={closeModal} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Annulla</button>
                                <button
                                    onClick={startUpload}
                                    disabled={!uploadItems.length || (!isPersonalDrive && uploadType === 'media' && soggettiList.length > 1 && !uploadSoggetto)}
                                    className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center gap-2 shadow-md">
                                    <UploadCloud size={18} /> Carica {uploadItems.length > 0 ? `${uploadItems.length} file` : ''}
                                </button>
                            </div>
                        </div>
                    )}

                    {modalStep === 'uploading' && (
                        <div className="space-y-4">
                            <div className="w-full bg-slate-100 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            {(() => {
                                const done = uploadItems.filter(i => ['ready','needs_date','error'].includes(i.status)).length;
                                const uploadedMB = (uploadedBytes / 1048576).toFixed(1);
                                const totalMB = (uploadTotalBytes / 1048576).toFixed(1);
                                const remainingB = Math.max(0, uploadTotalBytes - uploadedBytes);
                                const etaSec = uploadSpeedMBps && uploadSpeedMBps > 0.01
                                    ? remainingB / (uploadSpeedMBps * 1048576) : null;
                                const etaStr = etaSec !== null
                                    ? etaSec < 60 ? `~${Math.round(etaSec)}s` : `~${Math.floor(etaSec / 60)}m ${Math.round(etaSec % 60)}s`
                                    : null;
                                return (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500 font-medium">
                                            {done}/{uploadItems.length} file
                                            {uploadTotalBytes > 0 && <span className="text-slate-400"> · {uploadedMB} / {totalMB} MB</span>}
                                        </span>
                                        <span className="font-black text-blue-600 tabular-nums">
                                            {uploadSpeedMBps !== null && `${uploadSpeedMBps.toFixed(1)} MB/s`}
                                            {etaStr && <span className="text-slate-400 font-medium"> · {etaStr}</span>}
                                        </span>
                                    </div>
                                );
                            })()}
                            <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                {uploadItems.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
                                        <StatusIcon status={item.status} />
                                        <span className="text-xs font-medium text-slate-700 truncate flex-1">{item.file.name}</span>
                                        <span className="text-[10px] font-bold shrink-0">
                                            {item.status === 'ready' && item.detectedDate && <span className="text-emerald-600">{new Date(item.detectedDate).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                                            {item.status === 'needs_date' && <span className="text-amber-600">Data mancante</span>}
                                            {item.status === 'error' && <span className="text-rose-600">{item.error || 'Errore'}</span>}
                                            {item.status === 'uploading' && <span className="text-blue-500">Caricamento...</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {modalStep === 'confirm_dates' && (
                        <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
                                <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-800">{uploadItems.filter(i => i.status === 'needs_date').length} file senza data EXIF.</p>
                            </div>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                                {uploadItems.filter(i => i.status === 'needs_date').map((item, i) => (
                                    <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                                        <p className="text-xs font-bold text-slate-700 mb-2 truncate">{item.file.name}</p>
                                        <input type="datetime-local" className="w-full text-sm p-2 border border-slate-300 rounded-lg bg-white" value={item.manualDate}
                                            onChange={e => updateItem(uploadItems.indexOf(item), { manualDate: e.target.value })} />
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end gap-2">
                                <button onClick={closeModal} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg">Annulla</button>
                                <button onClick={confirmDates} disabled={isConfirming || uploadItems.filter(i => i.status === 'needs_date').some(i => !i.manualDate)}
                                    className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                                    {isConfirming ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
                                    Conferma
                                </button>
                            </div>
                        </div>
                    )}

                    {modalStep === 'completed' && completedSummary && (
                        <div className="space-y-5">
                            <div className="flex flex-col items-center text-center gap-4 py-3">
                                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                                    <CheckCircle2 size={28} className="text-emerald-500" />
                                </div>
                                <div>
                                    <p className="font-black text-slate-900 text-base">Caricamento completato</p>
                                    <p className="text-sm text-slate-500 mt-1.5">
                                        <span className="font-black text-emerald-600">{completedSummary.ok}</span> file caricati con successo
                                    </p>
                                    {completedSummary.errors > 0 && (
                                        <p className="text-xs text-rose-600 mt-1 font-bold">{completedSummary.errors} errori</p>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={closeCompleted}
                                className="w-full bg-emerald-600 text-white px-6 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md"
                            >
                                OK
                            </button>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default FileManagerView;
